// TemplateItemRow.tsx
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { observer } from 'mobx-react-lite';
import { type RefObject } from 'react';
import type { LessonReportItemTypeT } from 'types/shared/core/LessonReport';
import { useStore } from 'hooks/useStore';
import LessonReportTemplateAddStore from './LessonReportTemplateAddStore';
import Co from 'constants/frontend/shared/Co';
import { ServiceConfig } from 'constants/shared/ServiceConfig';

type TemplateItemRowPropsT = {
  index: number;
  isDragging: boolean;
  rowRef: RefObject<HTMLDivElement>;
  handleRef: RefObject<HTMLButtonElement>;
};

const TemplateItemRow = observer(function TemplateItemRow({
  index,
  isDragging,
  rowRef,
  handleRef,
}: TemplateItemRowPropsT) {
  const { removeRow, updateField, templateItems, setItemType } =
    useStore<LessonReportTemplateAddStore>('LessonReportTemplateAddStore');

  const item = templateItems[index];
  if (!item) return null;

  const handleTitleChange = (title: string) => {
    updateField(item.no, 'title', title);
  };

  const handleTypeChange = (type: LessonReportItemTypeT) => {
    setItemType(item.no, type);
  };

  const handleRemove = () => {
    removeRow(item.no);
  };

  return (
    <Paper
      ref={rowRef}
      elevation={1}
      style={{
        opacity: isDragging ? 0.6 : 1,
        padding: 12,
        borderRadius: 8,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* index 표시 (1-base) */}
        <Typography variant="body2" sx={{ width: 36, textAlign: 'right', opacity: 0.6 }}>
          {item.index + 1}
        </Typography>

        {/* 제목 */}
        <TextField
          size="small"
          label="항목명"
          required
          inputProps={{
            maxLength: ServiceConfig.LessonReport.maxItemTitleLength,
          }}
          value={item.title}
          onChange={(e) => handleTitleChange(e.target.value)}
          sx={{ flex: '0 0 150px', width: 150 }}
        />

        {/* 타입 선택 */}
        <TextField
          label="유형"
          select
          size="small"
          value={item.type}
          onChange={(e) => handleTypeChange(e.target.value as LessonReportItemTypeT)}
          sx={{ width: 150 }}
        >
          {Array.from(Co.Report.lessonReportTemplateItemTypeNames).map((itemType) => (
            <MenuItem key={itemType[0]} value={itemType[0]}>
              {itemType[1]}
            </MenuItem>
          ))}
        </TextField>

        {/* type별 옵션들 */}
        <Stack direction="row" spacing={1} flex={1} alignItems="center">
          {item.type === 'boolean' && (
            <Typography variant="caption" color="textSecondary">
              숙제여부 등
            </Typography>
          )}

          {item.type === 'select' && (
            <>
              <TextField
                size="small"
                label="가능한 항목 (콤마로 구분)"
                value={(item.selectOptions ?? []).join(', ')}
                onChange={(e) =>
                  updateField(
                    item.no,
                    'selectOptions',
                    e.target.value.split(',').map((s) => s.trim()),
                  )
                }
              />
              <Typography variant="caption" color="textSecondary">
                출석,지각,결석 등
              </Typography>
            </>
          )}

          {item.type === 'score' && (
            <>
              <TextField
                size="small"
                label="만점"
                type="number"
                value={item.maxScore ?? ''}
                onChange={(e) =>
                  updateField(
                    item.no,
                    'maxScore',
                    e.target.value === '' ? undefined : Number(e.target.value),
                  )
                }
                sx={{ width: 100 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!item.allowFractionalScore}
                    onChange={(e) => updateField(item.no, 'allowFractionalScore', e.target.checked)}
                  />
                }
                label="소수 허용"
              />
              <Typography variant="caption" color="textSecondary">
                미니테스트, 단어시험 등
              </Typography>
            </>
          )}

          {item.type === 'grade' && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!item.detailedGradeScale}
                    onChange={(e) => updateField(item.no, 'detailedGradeScale', e.target.checked)}
                  />
                }
                label="+ / - 등급"
              />
              <Typography variant="caption" color="textSecondary">
                수업태도 등, ABCDF형 평가
              </Typography>
            </>
          )}

          {item.type === 'text' && (
            <>
              <Select
                size="small"
                value={item.textVariant ?? 'short'}
                onChange={(e) =>
                  updateField(item.no, 'textVariant', e.target.value as 'short' | 'long')
                }
              >
                <MenuItem value="short">10자 이내 단문</MenuItem>
                <MenuItem value="long">장문</MenuItem>
              </Select>
              <Typography variant="caption" color="textSecondary">
                코멘트, 등급 등
              </Typography>
            </>
          )}
        </Stack>

        {/* 삭제 버튼 */}
        <IconButton aria-label="delete" onClick={handleRemove} size="small">
          <DeleteOutlineIcon />
        </IconButton>

        {/* 드래그 핸들 (실제 draggable 영역) */}
        <IconButton aria-label="drag handle" ref={handleRef} size="small">
          <DragIndicatorIcon />
        </IconButton>
      </Stack>
    </Paper>
  );
});

export default TemplateItemRow;
